import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatDateWithTimezoneOffset } from '../../../helpers/utils';
import type { RootState } from '../../../state/store';
import { ExportStatus } from '../helpers/constants';
import type { ExportHistoryItem } from '../types/exportTypes';

export interface TransformedExportRecord {
  id: string;
  generated_at: string;
  report_type: string;
  time_range: string;
  generated_by: string;
  expiry_date: string;
  locations: string;
  status: string;
  download_url: string | null;
  raw_status: ExportStatus;
  isExpired: boolean;
}

export const useTransformExportRecords = (
  exportHistory: ExportHistoryItem[]
): TransformedExportRecord[] => {
  const { company_details } = useSelector((state: RootState) => state.company);
  const locations = useSelector((state: RootState) => state.locations);

  return useMemo(() => {
    return exportHistory.map((x) => {
      const [startDate, endDate] = [
        formatDateWithTimezoneOffset(
          x.generated_for.start_date,
          company_details?.time_zone_offset,
          'MMM dd, yyyy'
        ),
        formatDateWithTimezoneOffset(
          x.generated_for.end_date,
          company_details?.time_zone_offset,
          'MMM dd, yyyy'
        )
      ];
      const generatedForLocations = locations
        .filter((l) => x.generated_for.location_ids.includes(l.id))
        .map((l) => l.name)
        .join(', ');

      // Check if report is expired
      const currentTime = Date.now() / 1000; // Convert to seconds
      const isExpired = x.expiry_date ? currentTime > x.expiry_date : false;

      return {
        id: x.id,
        generated_at: formatDateWithTimezoneOffset(
          x.generated_at,
          company_details?.time_zone_offset,
          'MMM dd, yyyy'
        ),
        report_type: x.name,
        time_range: `${startDate} - ${endDate}`,
        generated_by: x.generated_for.staff_name,
        expiry_date: x.expiry_date
          ? formatDateWithTimezoneOffset(
              x.expiry_date,
              company_details?.time_zone_offset,
              'MMM dd, yyyy'
            )
          : 'N/A',
        locations: generatedForLocations,
        status:
          x.status === ExportStatus.COMPLETED
            ? 'Completed'
            : x.status === ExportStatus.PENDING
              ? 'Pending'
              : x.status === ExportStatus.ERROR
                ? 'Error'
                : 'Unknown',
        download_url: x.download_url,
        raw_status: x.status,
        isExpired: isExpired
      };
    });
  }, [exportHistory, company_details, locations]);
};
